// Define proper interfaces for flag values
export interface FlagValue {
  enabled: boolean;
  value: any;
}

let getSharedFlag:
  | ((flagName: string) => { enabled: boolean; value?: any } | null)
  | null = null;

// Create a promise to handle the dynamic import
const getSharedFlagPromise = import(
  "../../../../../../apps/frontend-ace-client/src/config/initializeFlagsmithClient"
)
  .then((module) => {
    if (module && typeof module.getFlag === "function") {
      getSharedFlag = module.getFlag;
      return getSharedFlag;
    } else {
      return null;
    }
  })
  .catch(() => {
    return null;
  });

function waitForFlagsmithInit(maxAttempts = 50, intervalMs = 100) {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const checkFlagsmithReady = () => {
      attempts++;

      import(
        "../../../../../../apps/frontend-ace-client/src/config/initializeFlagsmithClient"
      )
        .then((module) => {
          if (module.initializeFlagsmithClient) {
            const client = module.initializeFlagsmithClient();

            if (client && client.initialised) {
              resolve(client);
              return;
            }
          }

          // If we've reached max attempts, resolve with null
          if (attempts >= maxAttempts) {
            resolve(null);
            return;
          }

          // Try again after interval
          setTimeout(checkFlagsmithReady, intervalMs);
        })
        .catch((error) => {
          console.log("error", error);
          if (attempts >= maxAttempts) {
            reject(error);
          } else {
            // Try again after interval
            setTimeout(checkFlagsmithReady, intervalMs);
          }
        });
    };

    // Start polling
    checkFlagsmithReady();
  });
}

// Make this function truly async so it can be awaited
export async function useFlagsWithLocalStorage<F extends string = string>(
  flagsToUse: F[],
): Promise<Record<F, FlagValue>> {
  // Wait for the Flagsmith client to be ready - using our polling approach
  try {
    await waitForFlagsmithInit();
  } catch (error) {
    // Silent catch
  }

  // Continue with the original implementation - this will now run after our waitForFlagsmithInit
  const resolvedGetFlag = await getSharedFlagPromise;

  // Return object for flag values
  const flagValues: Record<string, FlagValue> = {};

  // For each flag name
  for (const flag of flagsToUse) {
    // Initialize with a default value
    let result: FlagValue = {
      enabled: false,
      value: null,
    };
    let flagSource = "default";

    // PRIORITY 1: Check localStorage first (highest priority - user overrides)
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        const storedValue = window.localStorage.getItem(flag);

        if (storedValue !== null) {
          // Update with localStorage value
          result = {
            enabled: storedValue === "true",
            value: storedValue,
          };
          flagSource = "localStorage";
        }
      }
    } catch (e) {
      // Silent catch
    }

    // PRIORITY 2: Check direct flag access
    if (flagSource === "default" && resolvedGetFlag) {
      try {
        const flagsmithValue = resolvedGetFlag(flag);

        if (flagsmithValue) {
          // Update with Flagsmith value
          result = {
            enabled: flagsmithValue.enabled,
            value:
              flagsmithValue.value ||
              (flagsmithValue.enabled ? "true" : "false"),
          };
          flagSource = "flagsmith";
        }
      } catch (e) {
        // Silent catch
      }
    }

    // PRIORITY 3: Try the global helper if available
    if (flagSource === "default") {
      try {
        if (typeof window !== "undefined" && window.getDirectFlag) {
          const directFlag = window.getDirectFlag(flag);

          if (directFlag !== null) {
            // Even if the flag is disabled, we return its state
            result = {
              enabled: directFlag.enabled,
              value:
                directFlag.value !== null
                  ? directFlag.value
                  : directFlag.enabled
                    ? "true"
                    : "false",
            };
            flagSource = "direct access";
          }
        }
      } catch (e) {
        // Silent catch
      }
    }

    // Store the final result
    flagValues[flag] = result;
  }

  return flagValues as Record<F, FlagValue>;
}

export async function isFlagEnabled(flagName: string): Promise<boolean> {
  const flags = await useFlagsWithLocalStorage([flagName]);
  return !!flags[flagName]?.enabled;
}
